import { Flex, Text } from '@chakra-ui/react'
import React from 'react'
import { InfoPopup } from 'components/InfoPopup'
import { Label } from 'components/Label'
import { Link } from 'components/Link'
import { Seo } from 'components/Seo'
import { Table } from 'components/Table/Table'
import {
  NOT_APPLICABLE,
  PAGE_LENGTH,
  ROLLUP_STATUS_ICONS,
  ROLLUP_STATUS_LABELS,
  ROLLUP_STATUS_VARIANTS,
  TIMESTAMP_COLUMN_FORMAT,
} from 'constants/common'
import { Routes } from 'constants/routes'
import type { BlockListItem } from 'generated/reactQueryClient'
import { useGetBlocks } from 'generated/reactQueryClient'
import { useCursorPagination } from 'hooks/useCursorPagination'
import { useThemeColors } from 'hooks/useThemeColors'
import { DetailLayout } from 'layouts/DetailLayout/DetailLayout'
import { getGasUsedPercentage } from 'utils/common'
import {
  getTimestampColumnFormatValue,
  getTimestampColumnTooltipFormatValue,
  getTimestampColumnTooltipText,
} from 'utils/dayjs'

const BlocksPage = () => {
  const COLORS = useThemeColors()
  const { cursor, setCursor, direction, setDirection } = useCursorPagination()
  const [isAgeTimestampFormat, setIsAgeTimestampFormat] = React.useState<boolean>(true)

  const { data, isLoading } = useGetBlocks({
    limit: PAGE_LENGTH,
    ...(cursor && { [direction]: cursor }),
  })

  const timestampFormat = isAgeTimestampFormat
    ? TIMESTAMP_COLUMN_FORMAT.AGE
    : TIMESTAMP_COLUMN_FORMAT.DATE_TIME

  return (
    <>
      <Seo title="Blocks" />
      <Table
        data={data?.data ?? []}
        hasNextPage={!!data?.paginationContext?.next}
        hasPreviousPage={!!data?.paginationContext?.previous}
        onNextPage={() => {
          setDirection('next')
          setCursor(data?.paginationContext?.next ?? '')
        }}
        onPreviousPage={() => {
          setDirection('previous')
          setCursor(data?.paginationContext?.previous ?? '')
        }}
        title="L2 Blocks"
        isLoading={isLoading}
        renderMobileRow={(row: BlockListItem) => (
          <Table.MobileRow key={row.blockIndex}>
            {({ isExpanded }) => (
              <Flex flex="1" flexDirection="column" textAlign="left" gap={3}>
                <Table.MobileRowCell label="Block:">
                  <Link
                    href={Routes.blockDetail({ id: String(row.blockIndex) })}
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    {row.blockIndex}
                  </Link>
                </Table.MobileRowCell>
                <Table.AnimatedMobileRowCell
                  label={
                    data?.data?.some((tx) => tx.timestamp) ? (
                      <Table.InteractiveMobileHeaderCell
                        variant="text3regular"
                        onClick={() => {
                          setIsAgeTimestampFormat((prev) => !prev)
                        }}
                      >
                        {timestampFormat}:
                      </Table.InteractiveMobileHeaderCell>
                    ) : (
                      `${timestampFormat}:`
                    )
                  }
                  isVisible={isExpanded}
                >
                  <InfoPopup
                    title={
                      timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE
                        ? 'Block Datetime:'
                        : 'Block Age:'
                    }
                    triggerElement={
                      <Text
                        variant="text3regular"
                        color={COLORS.grey03}
                        whiteSpace="nowrap"
                        onClick={(event) => {
                          event.stopPropagation()
                        }}
                      >
                        {row.timestamp
                          ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                          : NOT_APPLICABLE}
                      </Text>
                    }
                  >
                    {row.timestamp
                      ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                      : NOT_APPLICABLE}
                  </InfoPopup>
                </Table.AnimatedMobileRowCell>
                <Table.MobileRowCell label="Txn:">
                  <Link
                    href={Routes.transactions({ block: row.blockIndex })}
                    display="inline-flex"
                    onClick={(event) => {
                      event.stopPropagation()
                    }}
                  >
                    {row.txCount.reduce((acc, curr) => acc + curr.count, 0)}
                  </Link>
                </Table.MobileRowCell>
                <Table.MobileRowCell label="Rollup Status:">
                  <Label
                    whiteSpace="noWrap"
                    variant={ROLLUP_STATUS_VARIANTS[row.rollupStatus]}
                    icon={ROLLUP_STATUS_ICONS[row.rollupStatus]}
                  >
                    {ROLLUP_STATUS_LABELS[row.rollupStatus]}
                  </Label>
                </Table.MobileRowCell>
                <Table.AnimatedMobileRowCell label="Gas Used:" isVisible={isExpanded}>
                  <Text variant="text3regular">
                    {row.gasUsed.toLocaleString()} (
                    {getGasUsedPercentage(Number(row.gasUsed), Number(row.gasLimit))})
                  </Text>
                </Table.AnimatedMobileRowCell>
                <Table.AnimatedMobileRowCell label="Gas Limit:" isVisible={isExpanded}>
                  <Text variant="text3medium">{Number(row.gasLimit)?.toLocaleString() ?? 0}</Text>
                </Table.AnimatedMobileRowCell>
              </Flex>
            )}
          </Table.MobileRow>
        )}
        renderTableHeader={() => (
          <>
            <Table.HeaderCell>Block</Table.HeaderCell>
            {data?.data?.some((block) => block.timestamp) ? (
              <Table.InteractiveHeaderCell
                tooltipText={getTimestampColumnTooltipText(timestampFormat)}
                onClick={() => {
                  setIsAgeTimestampFormat((prev) => !prev)
                }}
              >
                {timestampFormat}
              </Table.InteractiveHeaderCell>
            ) : (
              <Table.HeaderCell>{timestampFormat}</Table.HeaderCell>
            )}
            <Table.HeaderCell>Txn</Table.HeaderCell>
            <Table.HeaderCell>Rollup Status</Table.HeaderCell>
            <Table.HeaderCell>Gas Used</Table.HeaderCell>
            <Table.HeaderCell>Gas Limit</Table.HeaderCell>
          </>
        )}
        renderTableRow={(row: BlockListItem) => (
          <Table.Row key={row.blockIndex}>
            <Table.RowCell>
              <Link href={Routes.blockDetail({ id: String(row.blockIndex) })}>
                {row.blockIndex}
              </Link>
            </Table.RowCell>
            <Table.RowCell>
              <InfoPopup
                title={
                  timestampFormat === TIMESTAMP_COLUMN_FORMAT.AGE ? 'Block Datetime:' : 'Block Age:'
                }
                triggerElement={
                  <Text variant="text3regular" color={COLORS.grey03} whiteSpace="nowrap">
                    {row.timestamp
                      ? getTimestampColumnFormatValue(row.timestamp, timestampFormat)
                      : NOT_APPLICABLE}
                  </Text>
                }
              >
                {row.timestamp
                  ? getTimestampColumnTooltipFormatValue(row.timestamp, timestampFormat)
                  : NOT_APPLICABLE}
              </InfoPopup>
            </Table.RowCell>
            <Table.RowCell>
              <Link href={Routes.transactions({ block: row.blockIndex })} display="inline-flex">
                {row.txCount.reduce((acc, curr) => acc + curr.count, 0)}
              </Link>
            </Table.RowCell>
            <Table.RowCell>
              <Label
                variant={ROLLUP_STATUS_VARIANTS[row.rollupStatus]}
                icon={ROLLUP_STATUS_ICONS[row.rollupStatus]}
              >
                {ROLLUP_STATUS_LABELS[row.rollupStatus]}
              </Label>
            </Table.RowCell>
            <Table.RowCell>
              <Text variant="text3regular">
                {row.gasUsed.toLocaleString()} (
                {getGasUsedPercentage(Number(row.gasUsed), Number(row.gasLimit))})
              </Text>
            </Table.RowCell>
            <Table.RowCell>
              <Text variant="text3medium">{Number(row.gasLimit)?.toLocaleString() ?? 0}</Text>
            </Table.RowCell>
          </Table.Row>
        )}
      />
    </>
  )
}

BlocksPage.getLayout = (page: React.ReactNode) => <DetailLayout>{page}</DetailLayout>

export default BlocksPage
